/**
 * The project store: one immutable ProjectState, mutated only
 * through these actions, autosaved to browser storage on every
 * change (brief 4.5).
 *
 * The store is also where the trial rules bite (decision S3):
 * display selectors live in gating.ts, and `requestExport` here is
 * the only export path — it refuses while unlicensed whatever the UI
 * shows. Computation is delegated to the injected ComputeApi (worker
 * pool in the browser, in-process in tests); licensing to the
 * injected LicenseVerifier (Block 6 transport, stub until then).
 */

import type {
  CollapseRule,
  IndicatorRule,
  RoleMap,
  SingletonRole,
} from '@sae/automation';
import { DEMO_SAMPLE_FILENAME, sampleCsvText } from '@sae/io';

import { initialProjectState } from './defaults.js';
import { exportsUnlocked } from './gating.js';
import type { LicenseVerifier, VerifyOutcome } from './license.js';
import type {
  CovariateMode,
  LicenseState,
  ProjectState,
  RunConfigState,
  SetupState,
  SourceKind,
  StepId,
} from './types.js';
import { STEP_ORDER } from './types.js';
import {
  EXPORT_LOCKED_MESSAGE,
  ExportRegistry,
  ExportUnavailableError,
} from '../exports/registry.js';
import type { ExportKind, ExportOutcome } from '../exports/registry.js';
import type { ProjectStorage } from '../persistence/idb.js';
import {
  distinctAreaIds,
  extractPopulationCounts,
} from '../pipeline/buildInputs.js';
import type { ComputeApi, PipelineJob } from '../worker/protocol.js';

export interface StoreDeps {
  readonly compute: ComputeApi;
  readonly storage: ProjectStorage;
  readonly verifier: LicenseVerifier;
  readonly exports: ExportRegistry;
  readonly autosaveDelayMs?: number;
}

/** Which pipeline a classified file runs on. */
export function pathForKind(
  kind: string,
): 'microdata' | 'area-level' {
  return kind === 'area-level' || kind === 'area-covariates'
    ? 'area-level'
    : 'microdata';
}

/**
 * Whether a step's prerequisites are met in the current state —
 * that is, whether the step has content to show. Since the owner's
 * 2026-10-18 flow change the step bar navigates freely (any step
 * may be VISITED for inspection; a step without content renders its
 * empty state), so this predicate no longer gates navigation. It
 * still drives the step bar's completion indicator and the
 * furthest-step record, which advance only on work actually done.
 */
export function canEnterStep(state: ProjectState, step: StepId): boolean {
  switch (step) {
    case 'upload':
      return true;
    case 'configure':
      return state.setup !== null;
    case 'results':
    case 'diagnostics':
      return state.run.result !== null;
  }
}

/** Blocking QC issues currently on record (brief 5.2 step 4). */
export function blockingQcIssues(
  state: ProjectState,
): readonly { checkId: string; message: string }[] {
  if (state.qc === null) return [];
  const out: { checkId: string; message: string }[] = [];
  for (const result of state.qc) {
    for (const issue of result.issues) {
      if (issue.severity === 'blocking') {
        out.push({ checkId: issue.checkId, message: issue.message });
      }
    }
  }
  return out;
}

/** Why a run cannot start, or null when it can (Step 2's gate). */
export function runBlocker(state: ProjectState): string | null {
  if (state.setup === null || state.source.table === null) {
    return 'Load a data file first.';
  }
  if (state.source.tableMissing) {
    return 'The data file is not stored in this browser. Add the file again to run.';
  }
  const { roles } = state.setup;
  const path = pathForKind(state.setup.assessment.classification.kind);
  if (path === 'microdata') {
    if (roles.areaId === undefined || roles.weight === undefined) {
      return 'Confirm the area variable and the weight variable in Step 1.';
    }
    if (state.setup.selectedIndicators.length === 0) {
      return 'Select at least one indicator in Step 2.';
    }
  } else {
    if (
      roles.areaId === undefined ||
      roles.estimate === undefined ||
      roles.sampleSize === undefined
    ) {
      return 'Confirm the area, direct estimate, and sample size columns in Step 1.';
    }
    if (roles.variance === undefined && roles.standardError === undefined) {
      return 'Confirm the sampling variance (or standard error) column in Step 1.';
    }
  }
  const blocking = blockingQcIssues(state);
  if (blocking.length > 0) {
    return `A quality check blocks this run: ${blocking[0].message}`;
  }
  return null;
}

export class ProjectStore {
  readonly #deps: StoreDeps;
  #state: ProjectState;
  readonly #listeners = new Set<() => void>();
  #saveTimer: ReturnType<typeof setTimeout> | null = null;
  #saveChain: Promise<void> = Promise.resolve();

  constructor(deps: StoreDeps) {
    this.#deps = deps;
    this.#state = initialProjectState();
  }

  getState = (): ProjectState => this.#state;

  subscribe = (listener: () => void): (() => void) => {
    this.#listeners.add(listener);
    return () => {
      this.#listeners.delete(listener);
    };
  };

  #set(next: ProjectState): void {
    this.#state = next;
    for (const listener of this.#listeners) listener();
    this.#scheduleSave();
  }

  #update(fn: (state: ProjectState) => ProjectState): void {
    this.#set(fn(this.#state));
  }

  #scheduleSave(): void {
    if (this.#saveTimer !== null) clearTimeout(this.#saveTimer);
    const delay = this.#deps.autosaveDelayMs ?? 250;
    this.#saveTimer = setTimeout(() => {
      const snapshot = this.#state;
      this.#saveChain = this.#saveChain
        .then(() => this.#deps.storage.saveProject(snapshot))
        .catch(() => {
          // Storage can fail (quota, private mode). The project lives
          // in memory regardless; a failed autosave never breaks work.
        });
    }, delay);
  }

  /** Flush any pending autosave (tests, page-hide). */
  async flushAutosave(): Promise<void> {
    if (this.#saveTimer !== null) {
      clearTimeout(this.#saveTimer);
      this.#saveTimer = null;
      const snapshot = this.#state;
      this.#saveChain = this.#saveChain.then(() =>
        this.#deps.storage.saveProject(snapshot),
      );
    }
    await this.#saveChain;
  }

  // ---- Navigation --------------------------------------------------

  /**
   * Navigate to any step (owner-approved 2026-10-18: step tabs are
   * freely clickable for looking around). Visiting a step whose
   * prerequisites are not met shows its empty state and does NOT
   * advance furthestStep — navigation alone never marks progress.
   */
  goToStep(step: StepId): void {
    this.#update((s) => ({
      ...s,
      step,
      furthestStep:
        canEnterStep(s, step) &&
        STEP_ORDER.indexOf(step) > STEP_ORDER.indexOf(s.furthestStep)
          ? step
          : s.furthestStep,
    }));
  }

  #advanceTo(step: StepId): void {
    this.#update((s) => {
      const furthest =
        STEP_ORDER.indexOf(step) > STEP_ORDER.indexOf(s.furthestStep)
          ? step
          : s.furthestStep;
      return { ...s, step, furthestStep: furthest };
    });
  }

  renameProject(name: string): void {
    this.#update((s) => ({ ...s, projectName: name }));
  }

  setKeepDataWithProject(keep: boolean): void {
    this.#update((s) => ({ ...s, keepDataWithProject: keep }));
  }

  // ---- Intake --------------------------------------------------------

  /**
   * Parse and assess a user file in the worker, then initialize the
   * confirmed setup from the detection output (proposals only — the
   * user confirms or edits them in Steps 1 and 2).
   */
  async loadFile(bytes: Uint8Array, filename: string): Promise<void> {
    await this.#loadBytes(bytes, filename, 'own');
  }

  /** The built-in demo: the sample file itself, through the same path. */
  async loadDemo(): Promise<void> {
    const bytes = new TextEncoder().encode(sampleCsvText());
    await this.#loadBytes(bytes, DEMO_SAMPLE_FILENAME, 'demo');
  }

  async #loadBytes(
    bytes: Uint8Array,
    filename: string,
    kind: Exclude<SourceKind, 'none'>,
  ): Promise<void> {
    let read;
    try {
      read = await this.#deps.compute.parseFile(bytes, filename);
    } catch (err) {
      this.#update((s) => ({
        ...initialProjectState(),
        license: s.license,
        source: {
          kind,
          filename,
          format: null,
          table: null,
          tableMissing: false,
          readIssues: [
            {
              code: 'IO_READ_FAILED',
              severity: 'error',
              message:
                err instanceof Error
                  ? err.message
                  : 'The file could not be read.',
            },
          ],
        },
      }));
      return;
    }
    const assessed = await this.#deps.compute.assess(read.table);
    const setup = initialSetup(assessed);
    this.#update((s) => ({
      ...initialProjectState(),
      license: s.license,
      projectName:
        kind === 'demo' ? 'Sample project — Maine county poverty' : s.projectName,
      source: {
        kind,
        filename,
        format: read.table.format,
        table: read.table,
        tableMissing: false,
        readIssues: read.issues,
      },
      setup,
    }));
  }

  /** Load a companion covariate file (Step 1, optional). */
  async loadCovariatesFile(bytes: Uint8Array, filename: string): Promise<void> {
    const read = await this.#deps.compute.parseFile(bytes, filename);
    const assessed = await this.#deps.compute.assess(read.table);
    const roles = assessed.assessment.roles.roles;
    const counts = extractPopulationCounts(read.table, roles);
    this.#update((s) => ({
      ...s,
      covariatesSource: { filename, table: read.table, roles },
      setup:
        s.setup !== null && counts !== null
          ? { ...s.setup, populationCounts: counts }
          : s.setup,
      qc: null,
      feasibility: null,
      run: markStale(s.run),
    }));
  }

  clearCovariatesFile(): void {
    this.#update((s) => ({
      ...s,
      covariatesSource: null,
      setup:
        s.setup !== null ? { ...s.setup, populationCounts: null } : s.setup,
      qc: null,
      feasibility: null,
      run: markStale(s.run),
    }));
  }

  // ---- Setup edits (Steps 1 and 2) -----------------------------------
  //
  // Every edit invalidates the QC results and the feasibility preview
  // (both were computed from the previous setup) and marks an
  // existing run stale: its numbers belong to the setup that made it.

  #editSetup(fn: (setup: SetupState) => SetupState): void {
    this.#update((s) =>
      s.setup === null
        ? s
        : {
            ...s,
            setup: fn(s.setup),
            qc: null,
            feasibility: null,
            run: markStale(s.run),
          },
    );
  }

  /** Reassign a singleton role (area, weight, estimate, ...), or clear it. */
  setSingletonRole(role: SingletonRole, column: string | null): void {
    const key = SINGLETON_KEYS[role];
    this.#editSetup((setup) => {
      const roles: Record<string, unknown> = { ...setup.roles };
      if (column !== null) {
        // A column holds one primary role: free it from any other.
        for (const other of Object.values(SINGLETON_KEYS)) {
          if (other !== key && roles[other] === column) {
            delete roles[other];
          }
        }
        roles[key] = column;
      } else {
        delete roles[key];
      }
      return { ...setup, roles: roles as unknown as RoleMap };
    });
  }

  /**
   * Assign one column to a role (or unassign it), reconciling the
   * rest of the map: a column holds one primary role, and a
   * singleton role holds one column. Indicator assignment goes
   * through addIndicatorColumn instead (it must fetch the rule).
   */
  assignColumnRole(
    column: string,
    role: import('@sae/automation').RoleName | null,
  ): void {
    this.#editSetup((setup) => {
      const roles: RoleMap = {
        ...setup.roles,
        indicators: setup.roles.indicators.filter((c) => c !== column),
        subgroups: setup.roles.subgroups.filter((c) => c !== column),
        covariates: setup.roles.covariates.filter((c) => c !== column),
      };
      const mutable = roles as unknown as Record<string, unknown>;
      for (const key of Object.values(SINGLETON_KEYS)) {
        if (mutable[key] === column) delete mutable[key];
      }
      let { selectedIndicators, subgroupsSelected } = setup;
      if (role !== null) {
        if (role === 'indicator') {
          mutable.indicators = [...(roles.indicators ?? []), column];
        } else if (role === 'subgroup') {
          mutable.subgroups = [...(roles.subgroups ?? []), column];
          subgroupsSelected = [...new Set([...subgroupsSelected, column])];
        } else if (role === 'covariate') {
          mutable.covariates = [...(roles.covariates ?? []), column];
        } else {
          const key = SINGLETON_KEYS[role as SingletonRole];
          mutable[key] = column;
        }
      } else {
        selectedIndicators = selectedIndicators.filter((id) => id !== column);
        subgroupsSelected = subgroupsSelected.filter((v) => v !== column);
      }
      return { ...setup, roles, selectedIndicators, subgroupsSelected };
    });
  }

  /** Add a column as an indicator: fetch its proposed rule, then select it. */
  async addIndicatorColumn(column: string): Promise<void> {
    const table = this.#state.source.table;
    if (table === null || this.#state.setup === null) return;
    const rule = await this.#deps.compute.proposeIndicatorRule(table, column);
    this.#editSetup((setup) => ({
      ...setup,
      roles: {
        ...setup.roles,
        indicators: setup.roles.indicators.includes(column)
          ? setup.roles.indicators
          : [...setup.roles.indicators, column],
      },
      indicatorRules: { ...setup.indicatorRules, [column]: rule },
      selectedIndicators: rule.complete
        ? [...new Set([...setup.selectedIndicators, rule.id])]
        : setup.selectedIndicators,
    }));
  }

  setIndicatorSelected(indicatorId: string, on: boolean): void {
    this.#editSetup((setup) => ({
      ...setup,
      selectedIndicators: on
        ? [...new Set([...setup.selectedIndicators, indicatorId])]
        : setup.selectedIndicators.filter((id) => id !== indicatorId),
    }));
  }

  /** Replace an indicator rule with the user's edited version (AUT-03). */
  setIndicatorRule(rule: IndicatorRule): void {
    this.#editSetup((setup) => ({
      ...setup,
      indicatorRules: {
        ...setup.indicatorRules,
        [rule.sourceColumn]: rule,
      },
    }));
  }

  setSubgroupSelected(variable: string, on: boolean): void {
    this.#editSetup((setup) => ({
      ...setup,
      subgroupsSelected: on
        ? [...new Set([...setup.subgroupsSelected, variable])]
        : setup.subgroupsSelected.filter((v) => v !== variable),
    }));
  }

  /** Accept a collapse rule for a subgroup variable, or remove it. */
  setCollapse(variable: string, rule: CollapseRule | null): void {
    this.#editSetup((setup) => {
      const collapses = { ...setup.collapses };
      if (rule === null) delete collapses[variable];
      else collapses[variable] = rule;
      return { ...setup, collapses };
    });
  }

  setCovariateMode(name: string, mode: CovariateMode): void {
    this.#editSetup((setup) => ({
      ...setup,
      covariateModes: { ...setup.covariateModes, [name]: mode },
    }));
  }

  setEstimateScale(scale: 'proportion' | 'percent'): void {
    this.#editSetup((setup) => ({ ...setup, estimateScale: scale }));
  }

  setAreaIndicatorId(id: string): void {
    this.#editSetup((setup) => ({ ...setup, areaIndicatorId: id }));
  }

  setConfig(patch: Partial<RunConfigState>): void {
    this.#update((s) => ({
      ...s,
      config: { ...s.config, ...patch },
      feasibility: null,
      run: markStale(s.run),
    }));
  }

  selectCell(cellId: string): void {
    this.#update((s) => ({ ...s, selectedCellId: cellId }));
  }

  // ---- Pipeline actions ------------------------------------------------

  #buildJob(): PipelineJob | null {
    const s = this.#state;
    if (s.setup === null || s.source.table === null) return null;
    const setup = s.setup;
    return {
      path: pathForKind(setup.assessment.classification.kind),
      table: s.source.table,
      roles: setup.roles,
      indicatorRules: setup.selectedIndicators
        .map((id) => setup.indicatorRules[id])
        .filter((r): r is IndicatorRule => r !== undefined),
      collapses: setup.subgroupsSelected
        .map((v) => setup.collapses[v])
        .filter((r): r is CollapseRule => r !== undefined),
      subgroupsSelected: setup.subgroupsSelected,
      covariateModes: setup.covariateModes,
      companion:
        s.covariatesSource !== null &&
        s.covariatesSource.table !== null &&
        s.covariatesSource.roles !== null
          ? {
              table: s.covariatesSource.table,
              roles: s.covariatesSource.roles,
            }
          : null,
      populationCounts: setup.populationCounts,
      estimateScale: setup.estimateScale,
      areaIndicatorId: setup.areaIndicatorId,
      runConfig: s.config,
    };
  }

  /** Run the QC-01..QC-20 catalog against the confirmed setup. */
  async refreshQc(): Promise<void> {
    const s = this.#state;
    if (s.setup === null || s.source.table === null) return;
    const setup = s.setup;
    const companion = s.covariatesSource;
    const results = await this.#deps.compute.runQc(
      s.source.table,
      setup.assessment.profile,
      {
        roles: setup.roles,
        indicatorRules: setup.selectedIndicators
          .map((id) => setup.indicatorRules[id])
          .filter((r): r is IndicatorRule => r !== undefined),
        respondentTerm: setup.assessment.preset.respondentTerm,
        ...(setup.populationCounts !== null
          ? { populationCounts: setup.populationCounts }
          : {}),
        ...(companion !== null &&
        companion.table !== null &&
        companion.roles !== null &&
        companion.roles.areaId !== undefined
          ? {
              crossFileAreas: {
                areas: distinctAreaIds(companion.table, companion.roles.areaId),
                label: companion.filename ?? 'covariate file',
              },
              covariateTable: {
                table: companion.table,
                areaColumn: companion.roles.areaId,
                covariates: companion.roles.covariates,
              },
            }
          : {}),
      },
    );
    this.#update((state) => ({ ...state, qc: results }));
  }

  /** Compute the feasibility preview for the current setup (Step 2). */
  async refreshFeasibility(): Promise<void> {
    const job = this.#buildJob();
    if (job === null) return;
    const preview = await this.#deps.compute.previewFeasibility(job);
    this.#update((state) => ({ ...state, feasibility: preview }));
  }

  /** Run the analysis in the worker; progress streams into the state. */
  async run(): Promise<void> {
    if (this.#state.qc === null) await this.refreshQc();
    const blocker = runBlocker(this.#state);
    if (blocker !== null) {
      this.#update((s) => ({
        ...s,
        run: { ...s.run, status: 'error', error: blocker },
      }));
      return;
    }
    const job = this.#buildJob();
    if (job === null) return;
    this.#update((s) => ({
      ...s,
      run: {
        status: 'running',
        progress: [],
        result: null,
        stale: false,
        error: null,
        startedAt: new Date().toISOString(),
        finishedAt: null,
      },
    }));
    try {
      const result = await this.#deps.compute.run(job, (event) => {
        this.#update((s) =>
          s.run.status === 'running'
            ? { ...s, run: { ...s.run, progress: [...s.run.progress, event] } }
            : s,
        );
      });
      const firstFitted =
        result.cells.find((c) => c.status === 'fitted') ?? result.cells[0];
      this.#update((s) => ({
        ...s,
        run: {
          status: 'done',
          progress: s.run.progress,
          result,
          stale: false,
          error: null,
          startedAt: s.run.startedAt,
          finishedAt: new Date().toISOString(),
        },
        selectedCellId: firstFitted?.cellId ?? s.selectedCellId,
      }));
      this.#advanceTo('results');
    } catch (err) {
      this.#update((s) => ({
        ...s,
        run: {
          ...s.run,
          status: 'error',
          error:
            err instanceof Error ? err.message : 'The run failed unexpectedly.',
          finishedAt: new Date().toISOString(),
        },
      }));
    }
  }

  // ---- License -----------------------------------------------------------

  /** Verify an email + key pair; on success the tool unlocks (D3). */
  async verifyLicense(email: string, key: string): Promise<VerifyOutcome> {
    const outcome = await this.#deps.verifier.verify(email, key);
    if (outcome.ok) {
      const pass = outcome.pass;
      this.#update((s) => ({
        ...s,
        license: {
          status: 'licensed',
          email: pass.email,
          verifiedAt: pass.verifiedAt,
          expiresAt: pass.licenseExpiresAt,
          lastError: null,
        },
      }));
      await this.#deps.storage.saveLicense(pass);
    } else {
      this.#update((s) => ({
        ...s,
        license: { ...s.license, lastError: outcome.message },
      }));
    }
    return outcome;
  }

  /** Forget the license on this machine (the license itself is unaffected). */
  async signOutLicense(): Promise<void> {
    this.#update((s) => ({
      ...s,
      license: {
        status: 'trial',
        email: null,
        verifiedAt: null,
        expiresAt: null,
        lastError: null,
      } satisfies LicenseState,
    }));
    await this.#deps.storage.saveLicense(null);
  }

  // ---- Exports -------------------------------------------------------------

  /**
   * The only export path. Locked while unlicensed (S3), whatever the
   * UI renders; Block 5 generators answer through the registry.
   */
  async requestExport(kind: ExportKind): Promise<ExportOutcome> {
    const state = this.#state;
    if (!exportsUnlocked(state.license)) {
      return { status: 'locked', kind, message: EXPORT_LOCKED_MESSAGE };
    }
    if (state.run.result === null) {
      return {
        status: 'no-results',
        kind,
        message: 'Run the model first; exports are generated from a completed run.',
      };
    }
    try {
      const artifact = await this.#deps.exports.generate(kind, { state });
      return { status: 'ready', kind, artifact };
    } catch (err) {
      if (err instanceof ExportUnavailableError) {
        return { status: 'unavailable', kind, message: err.message };
      }
      return {
        status: 'unavailable',
        kind,
        message: err instanceof Error ? err.message : String(err),
      };
    }
  }

  // ---- Persistence -----------------------------------------------------------

  /** Restore the autosaved project and license, if any. */
  async restore(): Promise<boolean> {
    const loaded = await this.#deps.storage.loadProject();
    if (loaded === null) return false;
    // Legacy rename (2026-10-18, Decision Log Addendum 22):
    // projects autosaved before the de-US naming pass carry
    // disclosurePresetId 'nchs'; the preset is now 'standard'
    // (identical rules), so migrate it on load.
    const saved: ProjectState =
      (loaded.config.disclosurePresetId as string) === 'nchs'
        ? {
            ...loaded,
            config: { ...loaded.config, disclosurePresetId: 'standard' },
          }
        : loaded;
    this.#set(saved);
    const pass = await this.#deps.storage.loadLicense();
    if (pass === null) {
      if (saved.license.status === 'licensed') {
        this.#update((s) => ({
          ...s,
          license: {
            status: 'trial',
            email: null,
            verifiedAt: null,
            expiresAt: null,
            lastError:
              'The license stored on this machine could not be found. Enter your email address and license key to unlock again.',
          },
        }));
      }
      return true;
    }
    if (new Date(pass.licenseExpiresAt).getTime() <= Date.now()) {
      this.#update((s) => ({
        ...s,
        license: {
          status: 'trial',
          email: pass.email,
          verifiedAt: null,
          expiresAt: pass.licenseExpiresAt,
          lastError:
            'This license has expired. Renew it on the product page to keep using the full tool.',
        },
      }));
      return true;
    }
    this.#update((s) => ({
      ...s,
      license: {
        status: 'licensed',
        email: pass.email,
        verifiedAt: pass.verifiedAt,
        expiresAt: pass.licenseExpiresAt,
        lastError: null,
      },
    }));
    return true;
  }

  /** Start over: a fresh project; the license on this machine is kept. */
  newProject(): void {
    this.#update((s) => ({
      ...initialProjectState(),
      license: s.license,
    }));
  }

  /** Delete all local data: project, license record, everything (brief 4.5). */
  async deleteAllLocalData(): Promise<void> {
    await this.#deps.storage.deleteAll();
    this.#set(initialProjectState());
  }
}

const SINGLETON_KEYS: Record<SingletonRole, string> = {
  'area-id': 'areaId',
  'area-name': 'areaName',
  weight: 'weight',
  stratum: 'stratum',
  psu: 'psu',
  'psu-population-count': 'psuPopulationCount',
  'respondent-id': 'respondentId',
  estimate: 'estimate',
  variance: 'variance',
  'standard-error': 'standardError',
  'sample-size': 'sampleSize',
  'population-count': 'populationCount',
};

function markStale(run: ProjectState['run']): ProjectState['run'] {
  return run.result !== null ? { ...run, stale: true } : run;
}

/**
 * Build the initial confirmed setup from an assessment: detected
 * roles stand as proposed, indicator rules with complete coverage
 * are preselected, proposed collapses are the default (brief 5.2
 * steps 5-6), every covariate starts on automatic selection, and the
 * estimate scale is proposed from the values — visible and editable
 * in Step 2, never silently applied (Q21).
 */
export function initialSetup(assessed: {
  assessment: import('@sae/automation').Assessment;
  indicatorRules: Readonly<Record<string, IndicatorRule>>;
  collapseProposals: Readonly<Record<string, CollapseRule | null>>;
}): SetupState {
  const { assessment } = assessed;
  const roles = assessment.roles.roles;
  const collapses: Record<string, CollapseRule> = {};
  for (const [variable, proposal] of Object.entries(
    assessed.collapseProposals,
  )) {
    if (proposal !== null && proposal !== undefined) {
      collapses[variable] = proposal;
    }
  }
  const covariateModes: Record<string, CovariateMode> = {};
  for (const name of roles.covariates) covariateModes[name] = 'auto';

  let estimateScale: 'proportion' | 'percent' = 'proportion';
  if (roles.estimate !== undefined) {
    const profile = assessment.profile.columns.find(
      (c) => c.name === roles.estimate,
    );
    if (profile?.numeric !== null && profile?.numeric !== undefined) {
      if (profile.numeric.max > 1) estimateScale = 'percent';
    }
  }

  return {
    assessment,
    roles,
    indicatorRules: assessed.indicatorRules,
    selectedIndicators: Object.values(assessed.indicatorRules)
      .filter((rule) => rule.complete)
      .map((rule) => rule.id),
    areaIndicatorId: roles.estimate ?? 'indicator',
    estimateScale,
    collapses,
    subgroupsSelected: [...roles.subgroups],
    covariateModes,
    populationCounts: null,
  };
}
